import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../api/axiosConfig';

/**
 * 구역을 더하고 이름을 고치는 화면.
 *
 * 예전에는 여기서 구역에 점검표를 붙였다. 그 값은 이제 점검에 쓰이지 않는다 --
 * 무엇으로 점검할지는 계획의 정거장이 정한다. 고를 수 있게 두면 관리자가
 * 붙여 놓고 "이 구역 점검을 정했다" 고 생각하는데, 아무 일도 일어나지 않는다.
 *
 * 대신 이 구역을 무엇이 도는지 보여준다. 고치는 자리는 계획이지만, 여기서
 * 이름을 바꾸거나 지우려는 사람은 그게 어디에 걸려 있는지 알아야 한다.
 */
const SectionFormPage = () => {
  const [name, setName] = useState('');
  const [coveredBy, setCoveredBy] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { facilityId, sectionId } = useParams();

  const isEditMode = !!sectionId;
  const canSubmit = useMemo(() => name.trim() !== '', [name]);

  useEffect(() => {
    if (!isEditMode) return;
    let alive = true;

    api
      .get(`/sections/${sectionId}`)
      .then((res) => alive && setName(res.data.name || ''))
      .catch((err) => {
        console.error('Error fetching section:', err);
        if (alive) setError('구역을 불러오지 못했습니다.');
      });

    api
      .get(`/inspection-plans/coverage/facility/${facilityId}`)
      .then((res) => {
        if (!alive) return;
        const found = res.data?.sections?.find((s) => s.id === Number(sectionId));
        setCoveredBy(found?.coveredBy ?? []);
      })
      .catch((err) => console.error('Error fetching coverage:', err));

    return () => {
      alive = false;
    };
  }, [sectionId, facilityId, isEditMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    setError('');
    try {
      if (isEditMode) {
        await api.patch(`/sections/${sectionId}`, { name: name.trim() });
      } else {
        await api.post('/sections', { name: name.trim(), facilityId: +facilityId });
      }
      navigate(`/facilities/${facilityId}/edit`);
    } catch (err) {
      setError('구역 저장에 실패했습니다.');
      console.error('Error saving section:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-2 mb-4">
      <header className="d-flex justify-content-between align-items-center py-3 mb-3">
        <Link
          to={`/facilities/${facilityId}/edit`}
          className="text-decoration-none text-dark"
        >
          <i className="bi bi-arrow-left fs-4"></i>
        </Link>
        <h4 className="mb-0 fw-bold">{isEditMode ? '구역 수정' : '구역 추가'}</h4>
        <button
          form="section-form"
          type="submit"
          className="btn btn-primary"
          disabled={!canSubmit || loading}
        >
          {loading ? '저장중...' : isEditMode ? '수정' : '추가'}
        </button>
      </header>

      {error && <div className="alert alert-danger">{error}</div>}

      <form id="section-form" onSubmit={handleSubmit}>
        <label htmlFor="name" className="form-label fw-bold">
          구역 이름 *
        </label>
        <input
          type="text"
          className="form-control mb-4"
          id="name"
          name="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="예시) 8층 화장실"
          required
        />
      </form>

      {isEditMode && (
        <>
          <label className="form-label fw-bold">이 구역을 도는 점검</label>
          {/* 아직 안 왔을 때와 정말 없을 때를 갈라야 한다. 오는 사이에 붉은
              경고가 번쩍이면 놀라기만 하고 알려주는 것이 없다. */}
          {coveredBy === null ? (
            <div className="text-muted small">불러오는 중…</div>
          ) : coveredBy.length === 0 ? (
            <div className="text-danger small">
              <i className="bi bi-exclamation-triangle me-1"></i>
              어느 순회에도 들어 있지 않습니다. 점검 계획에서 넣어 주세요.
            </div>
          ) : (
            <div className="bg-light rounded p-3">
              {coveredBy.map((c, i) => (
                <div key={`${c.planId}-${i}`} className="small">
                  <i className="bi bi-calendar-check text-primary me-1"></i>
                  {c.planName}
                  {c.planStatus === 'PAUSED' && ' (멈춤)'}
                  <span className="mx-1 text-muted">›</span>
                  {c.templateName ?? <span className="text-danger">점검표 없음</span>}
                </div>
              ))}
              <div className="text-muted mt-2" style={{ fontSize: '0.75rem' }}>
                점검 계획에서 바꿉니다.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SectionFormPage;
